import { Form, DatePicker, Input } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  Controller,
  Control,
  UseFormSetValue,
  UseFormTrigger,
  useFormState,
} from "react-hook-form";
import type { GetTitleJournalResponse } from "@/entities/api/journal/journal-title-types";
import "@/entities/journal";
import type { CompanyItem } from "@/shared/ui";
import { SectionThirdFormType } from "../../model/types";
import { SectionCompanyMultiSelect } from "../section-company-select";
import { GeneralInfoPlace } from "./ui";

dayjs.locale("ru");

interface SectionGeneralInfoProps {
  control: Control<SectionThirdFormType>;
  setValue: UseFormSetValue<SectionThirdFormType>;
  trigger: UseFormTrigger<SectionThirdFormType>;
  initialLocationType?: string;
  disabled?: boolean;
  titleJournal?: GetTitleJournalResponse;
  isCreateMode?: boolean;
  initialCompanyList?: CompanyItem[] | null;
}

export function SectionGeneralInfo({
  control,
  setValue,
  trigger,
  initialLocationType,
  disabled = false,
  titleJournal,
  isCreateMode = false,
  initialCompanyList,
}: SectionGeneralInfoProps) {
  const { errors } = useFormState({
    control,
    name: ["workDate", "workName", "workCondition"], 
  });
  return (
    <>
      <div className="journal-form-section-title">
        Общая информация
      </div>
      <div className="form-dates">
        <Form.Item
          label="Дата выполнения работ"
          validateStatus={errors.workDate ? "error" : ""}
          help={errors.workDate?.message?.toString()}
          style={{ marginBottom: 24 }}
        >
          <Controller
            name="workDate"
            control={control}
            rules={{ required: "Укажите дату начала выполнения работ" }}
            render={({ field }) => (
              <DatePicker
                {...field}
                style={{ width: 300 }}
                format="DD.MM.YYYY"
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => {
                  field.onChange(date?.format("YYYY-MM-DD") ?? '');
                  trigger("workDate");
                }}
                disabled={disabled}
              />
            )}
          />
        </Form.Item>
      </div>
      <Form.Item
        label="Наименование работ, выполняемых в процессе строительства, реконструкции, капитального ремонта объекта капитального строительства"
        validateStatus={errors.workName ? "error" : ""}
        help={errors.workName?.message?.toString()}
        style={{ marginBottom: 24 }}
      >
        <Controller
          name="workName"
          control={control}
          rules={{
            required:
              "Наименование работ, выполняемых в процессе строительства, реконструкции, капитального ремонта объекта капитального строительства",
          }}
          render={({ field }) => (
            <Input.TextArea
              {...field}
              placeholder=""
              disabled={disabled}
              maxLength={255}
              showCount
              autoSize={{ minRows: 2, maxRows: 6 }}
              onChange={(e) => field.onChange(e.target.value.slice(0, 255))}
            />
          )}
        />
      </Form.Item>
      <GeneralInfoPlace
        control={control}
        setValue={setValue}
        initialLocationType={initialLocationType}
        disabled={disabled}
      />
      <Form.Item
        label="Условия производства работ (необязательно)"
        validateStatus={errors.workCondition ? "error" : ""}
        help={errors.workCondition?.message?.toString()}
        style={{ marginBottom: 24, width: "50%" }}
      >
        <Controller
          name="workCondition"
          control={control}
          rules={{
            required:
              "Наименование работ, выполняемых в процессе строительства, реконструкции, капитального ремонта объекта капитального строительства",
          }}
          render={({ field }) => (
            <Input
              {...field}
              placeholder=""
              disabled={disabled}
              maxLength={255}
              showCount
              onChange={(e) => field.onChange(e.target.value.slice(0, 255))}
            />
          )}
        />
      </Form.Item>

      <SectionCompanyMultiSelect
        control={control}
        setValue={setValue}
        titleJournal={titleJournal}
        isCreateMode={isCreateMode}
        initialCompanyList={initialCompanyList}
        disabled={disabled}
      />
    </>
  );
}
